import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Users, Copy, Check, TrendUp, CurrencyEur, ArrowRight, Handshake,
  ShareNetwork, CircleNotch, X, Warning, ArrowLeft,
  TwitterLogo, LinkedinLogo, WhatsappLogo, EnvelopeSimple, Trophy, Crown,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Affiliate dashboard — /affiliates
 * Shows: referral code + link, 4 stat cards, referrals table, payout requests.
 * 20% recurring commission for 12 months. €50 min payout.
 */
export default function Affiliates() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showPayout, setShowPayout] = useState(false);
  const [payoutForm, setPayoutForm] = useState({ method: 'paypal', details: '' });
  const [payoutBusy, setPayoutBusy] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);
  const [lbScope, setLbScope] = useState('monthly');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/affiliates/me`, { withCredentials: true });
      setData(res.data);
    } catch {
      toast.error(t('aff.load_fail'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadLeaderboard = useCallback(async (scope) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/affiliates/leaderboard`, {
        params: { scope }, withCredentials: true,
      });
      setLeaderboard(res.data);
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLeaderboard(lbScope); }, [lbScope, loadLeaderboard]);

  const copyLink = useCallback(async () => {
    if (!data?.referral_link) return;
    try {
      await navigator.clipboard.writeText(data.referral_link);
      setCopied(true);
      toast.success(t('aff.link_copied'));
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t('aff.copy_fail'));
    }
  }, [data, t]);

  const submitPayout = async () => {
    if (!payoutForm.details.trim()) { toast.error(t('aff.details_required')); return; }
    setPayoutBusy(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/affiliates/payout-request`,
        payoutForm,
        { withCredentials: true },
      );
      toast.success(t('aff.payout_requested'));
      setShowPayout(false);
      setPayoutForm({ method: 'paypal', details: '' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('aff.request_fail'));
    } finally {
      setPayoutBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <CircleNotch size={48} className="text-[#A855F7] animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const s = data.stats;
  const shareMsg = t('aff.share_message');

  return (
    <div className="min-h-screen bg-[#050505] text-white p-6 lg:p-10">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          data-testid="aff-back-btn"
          className="flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6"
        >
          <ArrowLeft size={16} /> Dashboard
        </button>

        <header className="mb-8">
          <h1 className="text-3xl font-[Outfit] font-bold flex items-center gap-3">
            <Handshake size={30} weight="fill" className="text-[#A855F7]" />
            {t('aff.title')}
          </h1>
          <p className="text-white/60 mt-2 max-w-2xl">
            {t('aff.subtitle_1')} <span className="text-[#10B981] font-semibold">{t('aff.subtitle_rate', { rate: data.commission_rate_pct })}</span>{' '}
            {t('aff.subtitle_2', { months: data.commission_months_cap, min: data.min_payout_eur.toFixed(0) })}
          </p>
        </header>

        {/* Referral link */}
        <section className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-[#A855F7]/10 to-[#2563EB]/10 border border-[#A855F7]/30">
          <div className="text-xs uppercase tracking-[0.25em] text-white/50 mb-2">{t('aff.your_link')}</div>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch">
            <input
              readOnly
              value={data.referral_link}
              data-testid="aff-referral-link-input"
              onClick={(e) => e.target.select()}
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-4 py-3 font-mono text-sm outline-none focus:border-[#A855F7]"
            />
            <button
              onClick={copyLink}
              data-testid="aff-copy-link-btn"
              className="flex-shrink-0 px-6 py-3 bg-[#A855F7] hover:bg-[#9333EA] rounded-lg font-medium flex items-center justify-center gap-2 transition-all"
            >
              {copied ? <Check size={18} weight="bold" /> : <Copy size={18} />}
              {copied ? t('aff.copied') : t('aff.copy')}
            </button>
          </div>
          <div className="mt-3 text-xs text-white/50">
            {t('aff.code_label')} <span className="font-mono font-semibold text-white/80">{data.code}</span>
          </div>

          {/* Quick share buttons — pre-composed messages for X, LinkedIn, WhatsApp, Email */}
          <div className="mt-5 pt-5 border-t border-white/10">
            <div className="text-xs uppercase tracking-[0.25em] text-white/50 mb-3">{t('aff.share_earn')}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="aff-share-buttons">
              <ShareBtn
                href={buildTwitterUrl(data.referral_link, shareMsg)}
                icon={<TwitterLogo size={18} weight="fill" />}
                label="X / Twitter"
                color="hover:bg-black hover:border-white/30"
                testid="aff-share-x"
              />
              <ShareBtn
                href={buildLinkedInUrl(data.referral_link)}
                icon={<LinkedinLogo size={18} weight="fill" />}
                label="LinkedIn"
                color="hover:bg-[#0A66C2]/20 hover:border-[#0A66C2]/50"
                testid="aff-share-linkedin"
              />
              <ShareBtn
                href={buildWhatsAppUrl(data.referral_link, shareMsg)}
                icon={<WhatsappLogo size={18} weight="fill" />}
                label="WhatsApp"
                color="hover:bg-[#25D366]/20 hover:border-[#25D366]/50"
                testid="aff-share-whatsapp"
              />
              <ShareBtn
                href={buildEmailUrl(data.referral_link, t('aff.share_email_subject'), t('aff.share_email_body', { msg: shareMsg, link: data.referral_link }))}
                icon={<EnvelopeSimple size={18} weight="fill" />}
                label="Email"
                color="hover:bg-white/10 hover:border-white/30"
                testid="aff-share-email"
              />
            </div>
          </div>
        </section>

        {/* 4 Stat cards */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <StatCard
            icon={<Users size={20} weight="fill" className="text-blue-400" />}
            label={t('aff.stat_signups')}
            value={s.total_referrals}
            testid="aff-stat-signups"
          />
          <StatCard
            icon={<TrendUp size={20} weight="fill" className="text-[#10B981]" />}
            label={t('aff.stat_converted')}
            value={`${s.converted} · ${s.conversion_rate_pct}%`}
            testid="aff-stat-converted"
          />
          <StatCard
            icon={<CurrencyEur size={20} weight="fill" className="text-[#F59E0B]" />}
            label={t('aff.stat_pending')}
            value={`€${s.pending_balance_eur.toFixed(2)}`}
            highlight
            testid="aff-stat-pending"
          />
          <StatCard
            icon={<Handshake size={20} weight="fill" className="text-[#A855F7]" />}
            label={t('aff.stat_lifetime')}
            value={`€${s.total_earned_eur.toFixed(2)}`}
            testid="aff-stat-lifetime"
          />
        </section>

        {/* Payout CTA */}
        <section className="mb-8 p-5 rounded-xl bg-white/5 border border-white/10 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
          <div>
            <div className="font-semibold">{t('aff.cashout_title')}</div>
            <div className="text-sm text-white/60">
              {s.pending_balance_eur >= data.min_payout_eur
                ? t('aff.cashout_available', { amount: s.pending_balance_eur.toFixed(2) })
                : t('aff.cashout_away', { amount: (data.min_payout_eur - s.pending_balance_eur).toFixed(2) })}
            </div>
          </div>
          <button
            onClick={() => setShowPayout(true)}
            disabled={!s.can_request_payout}
            data-testid="aff-request-payout-btn"
            className={`flex-shrink-0 px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-all ${
              s.can_request_payout
                ? 'bg-[#10B981] hover:bg-[#059669] text-black'
                : 'bg-white/5 text-white/40 cursor-not-allowed border border-white/10'
            }`}
          >
            <CurrencyEur size={16} weight="bold" />
            {t('aff.request_payout')}
          </button>
        </section>

        {/* Referrals table */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Users size={18} /> {t('aff.referrals_title')} ({data.referrals.length})
          </h2>
          {data.referrals.length === 0 ? (
            <EmptyState
              icon={<ShareNetwork size={32} className="text-white/30" />}
              title={t('aff.no_referrals_title')}
              body={t('aff.no_referrals_body')}
            />
          ) : (
            <div className="rounded-xl border border-white/10 overflow-hidden">
              <table className="w-full text-sm" data-testid="aff-referrals-table">
                <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">{t('aff.th_user')}</th>
                    <th className="text-left px-4 py-3">{t('aff.th_plan')}</th>
                    <th className="text-left px-4 py-3">{t('aff.th_joined')}</th>
                    <th className="text-left px-4 py-3">{t('aff.th_status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.referrals.map((r) => (
                    <tr key={r.user_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 font-mono text-xs">{r.email_masked}</td>
                      <td className="px-4 py-3 capitalize">{r.plan}</td>
                      <td className="px-4 py-3 text-white/50">{formatDate(r.claimed_at, i18n.language)}</td>
                      <td className="px-4 py-3">
                        {r.converted ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30">
                            {t('aff.paid')}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-white/5 text-white/50 border border-white/10">
                            {t('aff.free')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Leaderboard */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Trophy size={18} weight="fill" className="text-[#F59E0B]" />
              {t('aff.top_earners')}
            </h2>
            <div className="flex bg-white/5 border border-white/10 rounded-lg p-1" data-testid="aff-leaderboard-scope">
              {['monthly', 'lifetime'].map((sc) => (
                <button
                  key={sc}
                  onClick={() => setLbScope(sc)}
                  data-testid={`aff-lb-scope-${sc}`}
                  className={`px-3 py-1 rounded text-xs capitalize transition-all ${
                    lbScope === sc ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'
                  }`}
                >
                  {sc === 'monthly' ? t('aff.this_month') : t('aff.all_time')}
                </button>
              ))}
            </div>
          </div>

          {leaderboard && leaderboard.top.length === 0 ? (
            <EmptyState
              icon={<Trophy size={32} className="text-white/30" />}
              title={t('aff.no_earnings_title')}
              body={t('aff.no_earnings_body')}
            />
          ) : leaderboard && (
            <>
              {/* User's rank (if outside top 20) */}
              {!leaderboard.me.in_top && leaderboard.me.rank && (
                <div
                  data-testid="aff-lb-my-rank-banner"
                  className="mb-3 px-4 py-3 bg-[#A855F7]/10 border border-[#A855F7]/30 rounded-lg text-sm flex items-center justify-between"
                >
                  <span>
                    {t('aff.your_rank')} <span className="font-bold text-[#A855F7]">#{leaderboard.me.rank}</span>
                    {' · '}
                    <span className="text-white/70">€{leaderboard.me.earned.toFixed(2)}</span>
                    {' · '}
                    <span className="text-white/50">{leaderboard.me.commissions} {t('aff.commissions')}</span>
                  </span>
                </div>
              )}

              <div className="rounded-xl border border-white/10 overflow-hidden">
                <table className="w-full text-sm" data-testid="aff-leaderboard-table">
                  <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-3 w-16">#</th>
                      <th className="text-left px-4 py-3">{t('aff.th_affiliate')}</th>
                      <th className="text-right px-4 py-3">{t('aff.th_referrals')}</th>
                      <th className="text-right px-4 py-3">{t('aff.th_commissions')}</th>
                      <th className="text-right px-4 py-3">{t('aff.th_earned')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.top.map((row) => (
                      <tr
                        key={row.user_id}
                        data-testid={`aff-lb-row-${row.rank}`}
                        className={`border-t border-white/5 ${
                          row.is_me ? 'bg-[#A855F7]/10' : 'hover:bg-white/[0.02]'
                        }`}
                      >
                        <td className="px-4 py-3 font-mono font-semibold">
                          {row.rank <= 3 ? (
                            <span className={`inline-flex items-center gap-1 ${
                              row.rank === 1 ? 'text-[#F59E0B]' : row.rank === 2 ? 'text-white/60' : 'text-[#C97C4C]'
                            }`}>
                              <Crown size={12} weight="fill" /> {row.rank}
                            </span>
                          ) : `#${row.rank}`}
                        </td>
                        <td className="px-4 py-3">
                          {row.display}
                          {row.is_me && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-widest bg-[#A855F7]/20 text-[#A855F7] border border-[#A855F7]/40">{t('aff.you')}</span>}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-white/60">{row.referrals}</td>
                        <td className="px-4 py-3 text-right font-mono text-white/60">{row.commissions}</td>
                        <td className="px-4 py-3 text-right font-mono font-semibold">€{row.earned_eur.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>

        {/* Payout history */}
        {data.payouts.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold mb-3">{t('aff.payout_history')}</h2>
            <div className="space-y-2" data-testid="aff-payouts-list">
              {data.payouts.map((p) => (
                <div key={p.payout_id} className="flex items-center justify-between px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-sm">
                  <div>
                    <span className="font-mono text-xs text-white/50 mr-3">{p.payout_id}</span>
                    <span className="capitalize">{p.method}</span> · {formatDate(p.created_at, i18n.language)}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono font-semibold">€{p.amount_eur.toFixed(2)}</span>
                    <StatusPill status={p.status} t={t} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Payout modal */}
        {showPayout && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowPayout(false)}>
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-6 max-w-md w-full"
              data-testid="aff-payout-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-[Outfit] font-bold">{t('aff.modal_title')}</h2>
                <button onClick={() => setShowPayout(false)} className="text-white/50 hover:text-white" data-testid="aff-payout-close">
                  <X size={20} />
                </button>
              </div>

              <div className="mb-5 px-4 py-3 rounded-lg bg-[#10B981]/10 border border-[#10B981]/30">
                <div className="text-xs uppercase tracking-widest text-[#10B981] mb-1">{t('aff.amount')}</div>
                <div className="text-2xl font-bold">€{s.pending_balance_eur.toFixed(2)}</div>
              </div>

              <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">{t('aff.method')}</label>
              <div className="grid grid-cols-3 gap-2 mb-5">
                {['paypal', 'wise', 'bank'].map((m) => (
                  <button
                    key={m}
                    onClick={() => setPayoutForm((f) => ({ ...f, method: m }))}
                    data-testid={`aff-payout-method-${m}`}
                    className={`py-2 rounded-lg text-sm capitalize border transition-all ${
                      payoutForm.method === m ? 'bg-[#A855F7]/20 border-[#A855F7] text-white' : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>

              <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">
                {payoutForm.method === 'paypal' ? t('aff.detail_paypal') : payoutForm.method === 'wise' ? t('aff.detail_wise') : t('aff.detail_bank')}
              </label>
              <textarea
                value={payoutForm.details}
                onChange={(e) => setPayoutForm((f) => ({ ...f, details: e.target.value }))}
                placeholder={
                  payoutForm.method === 'paypal' ? 'you@example.com' :
                  payoutForm.method === 'wise' ? '@yourtag or you@example.com' :
                  'IBAN: PT50 0000 0000 0000 0000 0000 0\nBIC: XXXXXXXX'
                }
                rows={3}
                data-testid="aff-payout-details"
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 mb-5 outline-none focus:border-[#A855F7] font-mono text-sm"
              />

              <div className="text-xs text-white/50 mb-5 flex items-start gap-2">
                <Warning size={14} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <span>{t('aff.payout_note')}</span>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPayout(false)}
                  className="flex-1 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm"
                  data-testid="aff-payout-cancel"
                >
                  {t('aff.cancel')}
                </button>
                <button
                  onClick={submitPayout}
                  disabled={payoutBusy || !payoutForm.details.trim()}
                  data-testid="aff-payout-submit"
                  className="flex-1 py-2 rounded-lg bg-[#10B981] hover:bg-[#059669] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium text-black flex items-center justify-center gap-2"
                >
                  {payoutBusy ? <CircleNotch size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  {payoutBusy ? t('aff.requesting') : t('aff.submit_request')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────

function StatCard({ icon, label, value, highlight, testid }) {
  return (
    <div
      data-testid={testid}
      className={`p-4 rounded-xl border transition-all ${
        highlight
          ? 'bg-[#F59E0B]/10 border-[#F59E0B]/30 hover:border-[#F59E0B]/50'
          : 'bg-white/5 border-white/10 hover:border-white/20'
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs uppercase tracking-widest text-white/50">{label}</span>
      </div>
      <div className="text-2xl font-bold font-[Outfit] truncate">{value}</div>
    </div>
  );
}

function EmptyState({ icon, title, body }) {
  return (
    <div className="p-8 rounded-xl border border-dashed border-white/10 flex flex-col items-center text-center">
      {icon}
      <div className="font-semibold mt-3">{title}</div>
      <div className="text-sm text-white/50 mt-1 max-w-sm">{body}</div>
    </div>
  );
}

function StatusPill({ status, t }) {
  const config = {
    pending: { bg: 'bg-[#F59E0B]/15', text: 'text-[#F59E0B]', border: 'border-[#F59E0B]/30' },
    paid: { bg: 'bg-[#10B981]/15', text: 'text-[#10B981]', border: 'border-[#10B981]/30' },
    rejected: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30' },
  }[status] || { bg: 'bg-white/5', text: 'text-white/50', border: 'border-white/10' };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${config.bg} ${config.text} ${config.border}`}>
      {t(`aff.status_${status}`, { defaultValue: status })}
    </span>
  );
}

function formatDate(iso, lang) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(lang === 'pt' ? 'pt-PT' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

// ── Share button + URL builders ────────────────────────────────

function ShareBtn({ href, icon, label, color, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className={`flex items-center justify-center gap-2 px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm transition-all ${color}`}
    >
      {icon} {label}
    </a>
  );
}

function buildTwitterUrl(link, msg) {
  const text = encodeURIComponent(`${msg} ${link}`);
  return `https://twitter.com/intent/tweet?text=${text}`;
}

function buildLinkedInUrl(link) {
  const url = encodeURIComponent(link);
  return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
}

function buildWhatsAppUrl(link, msg) {
  const text = encodeURIComponent(`${msg} ${link}`);
  return `https://wa.me/?text=${text}`;
}

function buildEmailUrl(link, subject, body) {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
